import type { ContentAccess } from '../types/agapellaSubscription';
import React, { useState } from 'react';
import { View, TouchableOpacity, Image, StyleSheet, Platform, ViewStyle } from 'react-native';
import Ionicons from '@expo/vector-icons/Ionicons';
import { useTheme } from '../context/ThemeContext';
import { useI18n } from '../i18n';
import { Typography } from './ui';
import { ShareModal } from './ShareModal';
import { buildShareText, shareOrOpenModal } from '../utils/roomShare';
import { typographyTokens } from '../styles/typographyTokens';

import { ACCENT_TEXT, ON_ACCENT_TEXT, inkOnFill } from '../styles/accentText';
import { surfaceFill } from '../styles/platformBackgrounds';
import { AgapellaBadge, badgeGap } from './subscription/AgapellaBadge';
export interface PracticeRoom {
    id: string;
    numericId?: number;
    title: string;
    description?: string;
    category: string;
    masterName: string;
    masterAvatar?: string;
    coverUrl?: string;
    participantCount: number;
    maxParticipants?: number;
    duration: string;
    status: 'live' | 'starting_soon' | 'open';
    startTime?: string;
    isPrivate?: boolean;
    mediaType?: 'video' | 'audio' | 'none';
    hostUserId?: string;
    /** Whether the authenticated user has subscribed to this room */
    isSubscribed?: boolean;
    /** CORE-1777 — «Входит в Agapēlla»: бейдж на карточке. */
    access?: ContentAccess;
}

interface PracticeRoomCardProps {
    room: PracticeRoom;
    onJoin: () => void;
    onPress: () => void;
    onEdit?: () => void;
    onDelete?: () => void;
    onShare?: () => void;
    isOwnRoom?: boolean;
    /** Whether the current user has subscribed to this room (server-driven) */
    isSubscribed?: boolean;
    /** 'grid' fills parent width, 'carousel' uses fixed 400px */
    variant?: 'grid' | 'carousel';
    /** Overrides the carousel width when 400px does not fit (mobile web rails). */
    carouselWidth?: number;
}

// CORE-1733 (возврат QA 23.10) — подпись бейджа подбирается по контрасту
// (inkOnFill): белый на янтарном «СКОРО» давал 2.15:1, на зелёном LIVE 2.28.
// «ОТКРЫТА» — #2563EB вместо #3B82F6: на том ни белый (3.68), ни тёмный (3.8)
// не держали 4.5.
const STATUS_CONFIG = {
    live: { color: '#22C55E', label: 'LIVE', labelEn: 'LIVE', icon: 'radio' as const },
    starting_soon: { color: '#F59E0B', label: 'СКОРО', labelEn: 'SOON', icon: 'time-outline' as const },
    open: { color: Platform.OS === 'web' ? '#2563EB' : '#3B82F6', label: 'ОТКРЫТА', labelEn: 'OPEN', icon: 'enter-outline' as const },
};

const COVERS = [
    require('../../assets/generated/agape_bali_yoga_sunrise_v2.jpg'),
    require('../../assets/generated/agape_minimalist_studio_stretch_v2.jpg'),
    require('../../assets/generated/agape_nature_meditation_lake_v2.jpg'),
];

export const PracticeRoomCard: React.FC<PracticeRoomCardProps> = ({
    room, onJoin, onPress, onEdit, onDelete, onShare, isOwnRoom, isSubscribed = false, variant = 'grid', carouselWidth,
}) => {
    const { isDark, colors: C } = useTheme();
    const { i18n } = useI18n();
    const language = i18n.language;
    const status = STATUS_CONFIG[room.status];
    const [showShare, setShowShare] = useState(false);
    // CORE-743 — one share text everywhere; the modal used to fall back to
    // `${title} — ${url}`.
    const share = buildShareText({ type: 'room', id: room.numericId ?? room.id, title: room.title });
    const shareAnalytics = { entityType: 'room', entityId: room.id, surface: 'practice_room_card' } as const;
    const cover = room.coverUrl
        ? { uri: room.coverUrl }
        : COVERS[parseInt(room.id, 10) % COVERS.length];

    const isCarousel = variant === 'carousel';
    const isOwnSoon = !!isOwnRoom && room.status !== 'live';

    const handleJoinPress = (e: any) => {
        e.stopPropagation?.();
        if (isOwnSoon && onShare) {
            onShare();
            return;
        }
        onJoin();
    };

    const getJoinLabel = (): string => {
        if (room.status === 'live') {
            if (isOwnRoom) return language === 'ru' ? 'ЗАЙТИ' : 'ENTER';
            return language === 'ru' ? 'ПРИСОЕДИНИТЬСЯ' : 'JOIN';
        }
        if (isOwnRoom) return language === 'ru' ? 'ПОДЕЛИТЬСЯ' : 'SHARE';
        if (isSubscribed) return language === 'ru' ? 'ИДУ  ✓' : 'GOING ✓';
        return language === 'ru' ? 'ПРИСОЕДИНИТЬСЯ' : 'JOIN';
    };

    const getJoinStyle = () => {
        if (isSubscribed && room.status !== 'live' && !isOwnRoom) {
            return { backgroundColor: '#22C55E' };
        }
        return {};
    };

    const getCategoryLabel = (category: string): string => {
        if (language !== 'ru') return category;
        const map: Record<string, string> = {
            Video: 'Видео',
            Audio: 'Аудио',
            Ambient: 'Атмосфера',
        };
        return map[category] || category;
    };

    return (
        <TouchableOpacity
            onPress={onPress}
            activeOpacity={0.88}
            style={[
                s.card,
                isCarousel && s.cardCarousel,
                isCarousel && carouselWidth != null && { width: carouselWidth },
                // CORE-1733 (возврат QA 23.10) — на вебе карточка комнаты — поверхность v4
                // (была плоская #FFFFFF / #1A1A1A на /practices). Натив — как был.
                Platform.OS === 'web' ? surfaceFill(isDark ? 'dark' : 'light') : {
                    backgroundColor: isDark ? '#1A1A1A' : '#FFFFFF',
                    borderColor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)',
                },
            ]}
        >
            {/* Cover */}
            <View style={s.coverWrap}>
                <Image source={cover} style={s.cover} resizeMode="cover" />
                <View style={s.coverOverlay} />

                {/* Status badge */}
                <View style={[s.statusBadge, { backgroundColor: status.color }]}>
                    <Ionicons name={status.icon} size={10} color={inkOnFill(status.color)} />
                    <Typography variant="captionAccent" style={[s.statusText, { color: inkOnFill(status.color) }]}>{language === 'ru' ? status.label : status.labelEn}</Typography>
                </View>

                {/* Own room badge */}
                {isOwnRoom && (
                    <View style={s.ownBadge}>
                        <Ionicons name="star" size={10} color={ON_ACCENT_TEXT} />
                        <Typography variant="captionAccent" style={s.ownBadgeText}>
                            {language === 'ru' ? 'ВАША' : 'YOURS'}
                        </Typography>
                    </View>
                )}

                {/* Participants pill */}
                <View style={s.participantsBadge}>
                    <Ionicons name="people" size={11} color="#FFF" />
                    <Typography variant="captionAccent" style={s.participantsText}>
                        {room.participantCount}{room.maxParticipants ? ` / ${room.maxParticipants}` : ''}
                    </Typography>
                </View>

                {/* Share */}
                <TouchableOpacity
                    // CORE-786 — a phone gets the OS sheet, a desktop the popup.
                    onPress={(e) => { e.stopPropagation?.(); void shareOrOpenModal({ url: share.url, title: room.title, subject: share.subject, message: share.message }, shareAnalytics, () => setShowShare(true)); }}
                    style={{ position: 'absolute', bottom: 10, left: 10, width: 28, height: 28, borderRadius: 14, backgroundColor: 'rgba(15,20,25,0.78)', alignItems: 'center', justifyContent: 'center' } as any}
                    accessibilityLabel="Поделиться"
                    activeOpacity={0.7}
                >
                    <Ionicons name="share-social-outline" size={15} color="#FFF" />
                </TouchableOpacity>
            </View>

            {/* Content */}
            <View style={s.content}>
                <View style={s.metaRow}>
                    <View style={[s.categoryTag, { backgroundColor: isDark ? 'rgba(232,93,4,0.15)' : 'rgba(232,93,4,0.08)' }]}>
                        <Typography variant="captionAccent" style={s.categoryText}>{getCategoryLabel(room.category)}</Typography>
                    </View>
                    {room.isPrivate && (
                        <Ionicons name="lock-closed" size={11} color={C.textSecondary} />
                    )}
                    <Typography variant="caption" style={{ color: C.textSecondary }}>
                        {room.duration}
                    </Typography>
                </View>

                <AgapellaBadge access={room.access} style={badgeGap} testID={`agapella-badge-room-${room.numericId ?? room.id}`} />
                <Typography variant="bodyAccent" numberOfLines={1} style={[s.title, { color: C.textPrimary }]}>
                    {room.title}
                </Typography>

                {room.description && (
                    <Typography variant="caption" numberOfLines={2} style={{ color: C.textSecondary }}>
                        {room.description}
                    </Typography>
                )}

                <View style={s.footerRow}>
                    <View style={s.masterRow}>
                        {room.masterAvatar ? (
                            <Image source={{ uri: room.masterAvatar }} style={s.avatar} />
                        ) : (
                            <View style={[s.avatar, s.avatarFallback]}>
                                <Ionicons name="person" size={9} color="#FFF" />
                            </View>
                        )}
                        <Typography variant="caption" style={{ color: C.textSecondary }} numberOfLines={1}>
                            {room.masterName}
                        </Typography>
                    </View>

                    <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
                        {isOwnRoom && room.status !== 'live' && onDelete && (
                            <TouchableOpacity
                                onPress={(e) => { e.stopPropagation?.(); onDelete(); }}
                                style={{ padding: 6, borderRadius: 8, backgroundColor: 'rgba(239,68,68,0.1)' }}
                                activeOpacity={0.7}
                            >
                                <Ionicons name="trash-outline" size={14} color="#EF4444" />
                            </TouchableOpacity>
                        )}
                        <TouchableOpacity
                            onPress={handleJoinPress}
                            style={[s.joinBtn, getJoinStyle(), isSubscribed && room.status !== 'live' && !isOwnRoom && s.joinBtnLocked]}
                            activeOpacity={isSubscribed && room.status !== 'live' && !isOwnRoom ? 1 : 0.8}
                            disabled={isSubscribed && room.status !== 'live' && !isOwnRoom}
                        >
                            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 4 }}>
                                {isOwnSoon && <Ionicons name="share-social" size={12} color={ON_ACCENT_TEXT} />}
                                <Typography variant="label" style={s.joinText}>
                                    {getJoinLabel()}
                                </Typography>
                            </View>
                        </TouchableOpacity>
                    </View>
                </View>
            </View>
            <ShareModal
                visible={showShare}
                onClose={() => setShowShare(false)}
                url={share.url}
                title={room.title}
                subject={share.subject}
                message={share.message}
                language={language}
                analytics={shareAnalytics}
            />
        </TouchableOpacity>
    );
};

const s = StyleSheet.create({
    card: {
        borderRadius: 20,
        borderWidth: 1,
        overflow: 'hidden',
        width: '100%',
        ...(Platform.OS === 'web' && {
            transition: 'transform 0.18s ease, box-shadow 0.18s ease',
            cursor: 'pointer',
        }),
    } as any,
    cardCarousel: {
        width: 400,
        flexShrink: 0,
    },
    coverWrap: {
        height: 225,
        position: 'relative',
    },
    cover: {
        width: '100%',
        height: '100%',
    } as any,
    coverOverlay: {
        ...StyleSheet.absoluteFillObject,
        backgroundColor: 'rgba(0,0,0,0.08)',
    },
    statusBadge: {
        position: 'absolute',
        top: 8,
        left: 8,
        flexDirection: 'row',
        alignItems: 'center',
        gap: 3,
        paddingHorizontal: 7,
        paddingVertical: 3,
        borderRadius: 6,
    } as ViewStyle,
    statusText: {
        ...typographyTokens.captionAccent,
        color: '#FFF',
    } as any,
    participantsBadge: {
        position: 'absolute',
        bottom: 8,
        right: 8,
        flexDirection: 'row',
        alignItems: 'center',
        gap: 4,
        backgroundColor: 'rgba(0,0,0,0.6)',
        paddingHorizontal: 7,
        paddingVertical: 3,
        borderRadius: 6,
    } as ViewStyle,
    ownBadge: {
        position: 'absolute',
        top: 8,
        right: 8,
        flexDirection: 'row',
        alignItems: 'center',
        gap: 3,
        backgroundColor: '#E98245',
        paddingHorizontal: 7,
        paddingVertical: 3,
        borderRadius: 6,
    } as ViewStyle,
    ownBadgeText: {
        ...typographyTokens.captionAccent,
        color: ON_ACCENT_TEXT,
    } as any,
    participantsText: {
        ...typographyTokens.captionAccent,
        color: '#FFF',
    } as any,
    content: {
        padding: 16,
        gap: 8,
    },
    metaRow: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
    },
    categoryTag: {
        paddingHorizontal: 8,
        paddingVertical: 3,
        borderRadius: 6,
    },
    categoryText: {
        ...typographyTokens.captionAccent,
        color: ACCENT_TEXT,
    } as any,
    title: {
        ...typographyTokens.bodyAccent,
    } as any,
    footerRow: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginTop: 2,
    },
    masterRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 6,
        flex: 1,
    },
    avatar: {
        width: 20,
        height: 20,
        borderRadius: 10,
    } as any,
    avatarFallback: {
        backgroundColor: '#888',
        alignItems: 'center',
        justifyContent: 'center',
    },
    joinBtn: {
        minHeight: 44,
        backgroundColor: '#E85D04',
        borderRadius: 16,
        paddingHorizontal: 20,
        paddingVertical: 10,
        alignItems: 'center',
        justifyContent: 'center',
        ...(Platform.OS === 'web' && {
            backgroundImage: 'linear-gradient(100deg, #E95420 0%, #FF7538 54%, #FFA45F 100%)',
            boxShadow: '0 8px 20px rgba(233,84,32,0.22)',
        }),
    },
    joinBtnLocked: {
        // Visual cue that the subscribed state cannot be toggled from the card
        ...(Platform.OS === 'web' && { cursor: 'default', backgroundImage: 'none', boxShadow: 'none' }),
    } as any,
    joinText: {
        ...typographyTokens.label,
        color: ON_ACCENT_TEXT,
    } as any,
});
